import React, { createContext, useContext, useEffect, useState } from "react";
import { AuditSession, sessionSchema } from "../models/heretic/auditSession";
import { fetchFastApi } from "../utils/fastApi";

interface AuditSessionContextType {
  auditSession: AuditSession | null;
  setAuditSession: React.Dispatch<React.SetStateAction<AuditSession | null>>;
  loading: boolean;
  fetchAuditSession: ({ auditSessionId }: { auditSessionId: string }) => Promise<void>;
}

const AuditSessionContext = createContext<AuditSessionContextType>({
  auditSession: null,
  setAuditSession: () => {},
  loading: false,
  fetchAuditSession: async () => {},
});

export const AuditSessionProvider = ({ children }: { children: React.ReactNode }) => {
  const [auditSession, setAuditSession] = useState<AuditSession | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (auditSession && auditSession.documentsState === "processing") {
      console.log("polling");
      const pollInterval = setInterval(() => {
        fetchAuditSession({ auditSessionId: auditSession.id, updateLoading: false });
      }, 5000);
      return () => clearInterval(pollInterval);
    }
  }, [auditSession]);

  const fetchAuditSession = async ({
    auditSessionId,
    updateLoading = true,
  }: {
    auditSessionId: string;
    updateLoading?: boolean;
  }) => {
    try {
      if (updateLoading) setLoading(true);
      const response = await fetchFastApi({
        path: `/matching/match-session/${auditSessionId}`,
        method: "GET",
      });
      const session = sessionSchema.parse(response.session);
      setAuditSession(session);
    } catch (error) {
      console.error(error);
    } finally {
      if (updateLoading) setLoading(false);
    }
  };
  return (
    <AuditSessionContext.Provider value={{ auditSession, setAuditSession, fetchAuditSession, loading }}>
      {children}
    </AuditSessionContext.Provider>
  );
};

export const useAudit = () => {
  const { auditSession, setAuditSession, fetchAuditSession, loading } = useContext(AuditSessionContext);
  return { auditSession, setAuditSession, fetchAuditSession, loading };
};
